import { masquerDonneesSensibles } from '@shared/donnees-sensibles.js'

/**
 * Ce que le replay de PostHog voit d'une page.
 *
 * - Champs de saisie : tous masqués (`*` de même longueur), SAUF ceux des
 *   zones de filtres des listes, marquées `data-replay-filtre` dans le kit
 *   (ListFiltersCard, ListPanelSearch, ListResultsBar…) : savoir ce que la
 *   personne a cherché ou filtré est ce qui rend un replay lisible. Un mot de
 *   passe reste masqué partout.
 * - Texte affiché : en clair, SAUF une zone marquée `data-replay-sensible`
 *   (adresse d'une personne, IBAN, date de naissance, handicap, santé),
 *   masquée en entier, comme les propositions d'adresse de Google Places,
 *   et les e-mails, téléphones, IBAN, BIC et numéros de
 *   sécurité sociale, masqués où qu'ils soient (shared/donnees-sensibles.js).
 * - Attributs (`data-text` des listes, `href="mailto:…"`, `title`…) : rrweb les
 *   recopie en clair, sans crochet de masquage. Ils sont nettoyés dans les
 *   snapshots par `before_send`, d'où des snapshots envoyés non compressés
 *   (la requête, elle, reste compressée).
 * - Adresses (page, requêtes, référent) : jetons, e-mails et téléphones retirés
 *   du chemin comme de la query (`/contacts/jean.dupont%40exemple.fr`).
 * - Réseau : ni corps ni en-têtes, quel que soit le réglage du projet PostHog.
 * - Autocapture des clics : ni texte ni attributs des éléments, liens nettoyés.
 *
 * Signatures vérifiées dans posthog-js 1.335 / @rrweb/record 2.0.0-alpha.17 :
 * `maskInputFn(texte, element)` reçoit le champ, `maskTextFn(texte, element)`
 * le parent du nœud texte (null possible).
 */

export const ATTRIBUT_FILTRE = 'data-replay-filtre'
export const ATTRIBUT_SENSIBLE = 'data-replay-sensible'

const SELECTEUR_FILTRE = `[${ATTRIBUT_FILTRE}]`
// Les propositions de Google Places (champ « Adresse » du kit, AdresseRecherche)
// sont posées par le script de Google sous <body>, hors de tout marquage.
const SELECTEUR_SENSIBLE = `[${ATTRIBUT_SENSIBLE}], .pac-container`
// Un éditeur de texte riche (Quill) ou une zone de saisie écrit dans des nœuds
// texte, pas dans une valeur de champ : il se masque comme un champ.
const SELECTEUR_SAISIE_TEXTE = 'textarea, [contenteditable]:not([contenteditable="false"]), .ql-editor'

// Les paramètres d'adresse qui valent un accès (lien de signature, laissez-passer…)
// ou qui désignent une personne (`/unsubscribe?email=`).
const PARAMETRE_SECRET = /token|jeton|secret|password|signature|code|key|e-?mail|phone|telephone/i

// rrweb enregistre chaque frappe : « jean.dupont@exem » ou « 06 12 34 56 » ne
// sont pas encore une adresse ou un numéro complets, mais en disent déjà trop.
const SAISIE_EN_COURS = [
  /@/,
  // Un numéro qui commence : « 06 12 », « +33 6 1 »…
  /^\s*(?:\+|0)[\d\s.()+-]*$/,
  // Un IBAN qui commence : « FR76 3000 ».
  /^\s*[A-Z]{2}\d{2}(?:\s?[A-Z0-9]){4,}\s*$/i,
]
const CHIFFRES_MIN_TELEPHONE = 4

function dansZone(element, selecteur) {
  return typeof element?.closest === 'function' && element.closest(selecteur) !== null
}

function estMotDePasse(element) {
  const type = element?.type || element?.getAttribute?.('type')
  return String(type || '').toLowerCase() === 'password'
}

function etoiles(texte) {
  return '*'.repeat(texte.length)
}

function etoilesSaufEspaces(texte) {
  return texte.replace(/\S/g, '*')
}

function ressembleASaisieSensible(valeur) {
  const [arobase, telephone, iban] = SAISIE_EN_COURS
  if (arobase.test(valeur) || iban.test(valeur)) return true
  return telephone.test(valeur) && (valeur.match(/\d/g) || []).length >= CHIFFRES_MIN_TELEPHONE
}

/**
 * mark.js découpe le texte d'une cellule autour du terme surligné :
 * « jean. » + <mark>dupont</mark> + « @exemple.fr ». Aucun fragment n'est
 * une adresse, le bloc entier en est une : c'est lui qu'on juge.
 */
function blocDecoupeParSurlignage(element) {
  if (!element || typeof element.getElementsByTagName !== 'function') return null
  const bloc = element.tagName === 'MARK' ? element.parentElement : element
  return bloc && bloc.getElementsByTagName('mark').length > 0 ? bloc : null
}

/** `session_recording.maskInputFn` : la valeur d'un champ. */
export function masquerSaisie(texte, element) {
  const valeur = String(texte ?? '')
  if (estMotDePasse(element)) return etoiles(valeur)
  if (dansZone(element, SELECTEUR_FILTRE) && !dansZone(element, SELECTEUR_SENSIBLE)) {
    // Une recherche par e-mail ou par téléphone reste une donnée personnelle,
    // dès la première frappe.
    return ressembleASaisieSensible(valeur) ? etoilesSaufEspaces(valeur) : masquerDonneesSensibles(valeur)
  }
  return etoiles(valeur)
}

/** `session_recording.maskTextFn` (avec `maskTextSelector: '*'`) : chaque texte affiché. */
export function masquerTexte(texte, element) {
  const valeur = String(texte ?? '')
  if (dansZone(element, SELECTEUR_SENSIBLE) || dansZone(element, SELECTEUR_SAISIE_TEXTE)) {
    return etoilesSaufEspaces(valeur)
  }
  const bloc = blocDecoupeParSurlignage(element)
  if (bloc) {
    const entier = bloc.textContent || ''
    if (masquerDonneesSensibles(entier) !== entier) return etoilesSaufEspaces(valeur)
  }
  return masquerDonneesSensibles(valeur)
}

/** Un morceau d'adresse (segment, valeur de paramètre), `*` s'il désigne quelqu'un. */
function morceauSansDonnee(morceau) {
  if (!morceau) return morceau
  let decode
  try {
    decode = decodeURIComponent(morceau.replace(/\+/g, ' '))
  } catch {
    decode = morceau
  }
  return masquerDonneesSensibles(decode) === decode ? morceau : '*'
}

function parametreSansDonnee(parametre) {
  const egal = parametre.indexOf('=')
  if (egal === -1) return morceauSansDonnee(parametre)
  const nom = parametre.slice(0, egal)
  return PARAMETRE_SECRET.test(nom) ? `${nom}=*` : `${nom}=${morceauSansDonnee(parametre.slice(egal + 1))}`
}

/**
 * Une adresse, sans ce qui vaut un accès ni ce qui désigne une personne :
 * valeurs des paramètres secrets, et tout segment du chemin ou toute valeur
 * qui contient un e-mail, un téléphone… même encodé (`jean.dupont%40exemple.fr`).
 */
export function adresseSansSecret(adresse) {
  if (typeof adresse !== 'string') return adresse
  const diese = adresse.indexOf('#')
  const avantFragment = diese === -1 ? adresse : adresse.slice(0, diese)
  const fragment = diese === -1 ? null : adresse.slice(diese + 1)
  const interrogation = avantFragment.indexOf('?')
  const chemin = interrogation === -1 ? avantFragment : avantFragment.slice(0, interrogation)
  const query = interrogation === -1 ? null : avantFragment.slice(interrogation + 1)

  let propre = chemin.split('/').map(morceauSansDonnee).join('/')
  if (query !== null) propre += `?${query.split('&').map(parametreSansDonnee).join('&')}`
  if (fragment !== null) propre += `#${fragment.split('&').map(parametreSansDonnee).join('&')}`
  return propre
}

/**
 * `session_recording.maskCapturedNetworkRequestFn` : la requête garde son
 * adresse (sans secret), sa méthode, son statut et ses durées ; jamais de corps
 * ni d'en-têtes. posthog-js l'appelle aussi avec `{ name: url }` seul pour
 * l'adresse de la page.
 */
export function retirerCorpsReseau(requete) {
  if (!requete) return requete
  const { requestBody, responseBody, requestHeaders, responseHeaders, ...reste } = requete
  if (typeof reste.name === 'string') reste.name = adresseSansSecret(reste.name)
  return reste
}

// Attributs qui portent une adresse : nettoyés comme une adresse.
const ATTRIBUTS_ADRESSE = new Set(['href', 'src', 'action', 'formaction', 'xlink:href', 'poster'])
// Attributs de mise en forme, parfois très longs (feuilles de style recopiées,
// images en data:, tracés SVG) : rien de personnel, et les passer au crible coûterait.
const ATTRIBUTS_MISE_EN_FORME = new Set(['class', 'style', '_cssText', 'rr_dataURL', 'srcset', 'd', 'points', 'viewBox', 'transform'])
const PROTOCOLE_PERSONNEL = /^(mailto|tel|sms|callto):/i
// Un lien de carte porte l'adresse dans son chemin ou sa query
// (`/maps/search/?api=1&query=12 rue…`, cf. data/DetailFieldValue.vue ;
// `waze.com/ul?q=12 rue…&navigate=yes`, cf. useFormationDetailV2).
const LIEN_CARTE = /^https?:\/\/(?:www\.)?(?:google\.[a-z.]+\/maps|waze\.com\/ul)\b/i
// Les propriétés d'un événement qui portent une adresse : `$current_url`,
// `$pathname`, `$referrer`, `$prev_pageview_pathname`, `$initial_current_url`…
const PROPRIETE_ADRESSE = /(?:url|pathname|referrer)$/i

/** Un lien : `mailto:`/`tel:` sans leur destinataire, une adresse sans secret. */
function lienSansDonnee(valeur) {
  if (/^data:/i.test(valeur)) return valeur
  const protocole = PROTOCOLE_PERSONNEL.exec(valeur)
  if (protocole) return `${protocole[0]}${etoiles(valeur.slice(protocole[0].length))}`
  const carte = LIEN_CARTE.exec(valeur)
  if (carte) return `${carte[0]}/*`
  return adresseSansSecret(valeur)
}

/** La valeur d'un attribut recopié par rrweb ou par l'autocapture. */
export function masquerAttribut(nom, valeur) {
  if (typeof valeur !== 'string' || !valeur || ATTRIBUTS_MISE_EN_FORME.has(nom)) return valeur
  if (ATTRIBUTS_ADRESSE.has(nom)) return lienSansDonnee(valeur)
  return masquerDonneesSensibles(valeur)
}

function attributsSansDonnee(attributs) {
  if (!attributs || typeof attributs !== 'object') return attributs
  const propres = {}
  for (const [nom, valeur] of Object.entries(attributs)) propres[nom] = masquerAttribut(nom, valeur)
  return propres
}

// Nœud sérialisé par rrweb : copié, jamais modifié en place (rrweb garde ses
// nœuds sérialisés dans son miroir).
function noeudSansDonnee(noeud) {
  if (!noeud || typeof noeud !== 'object') return noeud
  const copie = { ...noeud }
  if (noeud.attributes) copie.attributes = attributsSansDonnee(noeud.attributes)
  if (Array.isArray(noeud.childNodes)) copie.childNodes = noeud.childNodes.map(noeudSansDonnee)
  return copie
}

// Types d'événements et source de mutation de rrweb (@rrweb/types).
const RRWEB_SNAPSHOT_COMPLET = 2
const RRWEB_SNAPSHOT_INCREMENTAL = 3
const RRWEB_META = 4
const RRWEB_PERSONNALISE = 5
const RRWEB_SOURCE_MUTATION = 0

/**
 * Un événement rrweb, ses attributs et ses adresses nettoyés. Un événement
 * compressé (données en chaîne) passe tel quel : d'où `compress_events: false`.
 */
export function evenementReplaySansDonnee(evenement) {
  const donnees = evenement?.data
  if (!donnees || typeof donnees !== 'object') return evenement
  switch (evenement.type) {
    case RRWEB_SNAPSHOT_COMPLET:
      return { ...evenement, data: { ...donnees, node: noeudSansDonnee(donnees.node) } }
    case RRWEB_SNAPSHOT_INCREMENTAL:
      if (donnees.source !== RRWEB_SOURCE_MUTATION) return evenement
      return {
        ...evenement,
        data: {
          ...donnees,
          adds: Array.isArray(donnees.adds) ? donnees.adds.map((ajout) => ({ ...ajout, node: noeudSansDonnee(ajout?.node) })) : donnees.adds,
          attributes: Array.isArray(donnees.attributes)
            ? donnees.attributes.map((changement) => ({ ...changement, attributes: attributsSansDonnee(changement?.attributes) }))
            : donnees.attributes,
        },
      }
    case RRWEB_META:
      return typeof donnees.href === 'string' ? { ...evenement, data: { ...donnees, href: adresseSansSecret(donnees.href) } } : evenement
    case RRWEB_PERSONNALISE: {
      const charge = donnees.payload
      if (!charge || typeof charge !== 'object' || typeof charge.href !== 'string') return evenement
      return { ...evenement, data: { ...donnees, payload: { ...charge, href: adresseSansSecret(charge.href) } } }
    }
    default:
      return evenement
  }
}

function proprietesSansDonnee(proprietes) {
  if (!proprietes || typeof proprietes !== 'object') return proprietes
  const propres = { ...proprietes }
  for (const [nom, valeur] of Object.entries(proprietes)) {
    if (typeof valeur === 'string' && PROPRIETE_ADRESSE.test(nom)) propres[nom] = lienSansDonnee(valeur)
  }
  if (Array.isArray(proprietes.$snapshot_data)) {
    propres.$snapshot_data = proprietes.$snapshot_data.map(evenementReplaySansDonnee)
  }
  // Autocapture : le lien de l'élément cliqué part même avec
  // `mask_all_element_attributes` (posthog-js, autocapturePropertiesForElement).
  if (Array.isArray(proprietes.$elements)) {
    propres.$elements = proprietes.$elements.map((element) => {
      if (!element || typeof element !== 'object') return element
      const propre = { ...element }
      for (const [nom, valeur] of Object.entries(element)) {
        if (nom.startsWith('attr__')) propre[nom] = masquerAttribut(nom.slice('attr__'.length), valeur)
      }
      return propre
    })
  }
  if (typeof proprietes.$elements_chain === 'string') {
    propres.$elements_chain = masquerDonneesSensibles(
      proprietes.$elements_chain.replace(/(href=")((?:[^"\\]|\\.)*)"/g, (tout, debut, lien) => `${debut}${lienSansDonnee(lien)}"`),
    )
  }
  // Heatmaps : `$$heatmap` range les clics par adresse complète de la page
  // (query et fragment compris, posthog-js ne la nettoie que si
  // `mask_personal_data_properties` est posé). Deux adresses qui n'en font plus
  // qu'une voient leurs listes fusionnées.
  if (proprietes.$heatmap_data && typeof proprietes.$heatmap_data === 'object' && !Array.isArray(proprietes.$heatmap_data)) {
    const parAdresse = {}
    for (const [adresse, clics] of Object.entries(proprietes.$heatmap_data)) {
      const propre = adresseSansSecret(adresse)
      parAdresse[propre] = Array.isArray(clics) && Array.isArray(parAdresse[propre]) ? [...parAdresse[propre], ...clics] : clics
    }
    propres.$heatmap_data = parAdresse
  }
  if (typeof proprietes.$el_text === 'string') propres.$el_text = masquerDonneesSensibles(proprietes.$el_text)
  return propres
}

/**
 * `before_send` : chaque événement, avant l'envoi. Adresses (page, référent),
 * attributs recopiés dans les snapshots du replay, liens de l'autocapture.
 * Ne lève jamais : un événement qu'on ne sait pas lire part tel quel.
 */
export function evenementSansDonnee(evenement) {
  if (!evenement || typeof evenement !== 'object') return evenement
  try {
    const propre = { ...evenement, properties: proprietesSansDonnee(evenement.properties) }
    if (evenement.$set) propre.$set = proprietesSansDonnee(evenement.$set)
    if (evenement.$set_once) propre.$set_once = proprietesSansDonnee(evenement.$set_once)
    return propre
  } catch {
    return evenement
  }
}

/** Les options de `session_recording` qui portent le masquage. */
export const OPTIONS_MASQUAGE_REPLAY = Object.freeze({
  maskAllInputs: true,
  maskInputFn: masquerSaisie,
  maskTextSelector: '*',
  maskTextFn: masquerTexte,
  maskCapturedNetworkRequestFn: retirerCorpsReseau,
  // Même si le projet PostHog se met un jour à enregistrer corps et en-têtes,
  // le client garde le dernier mot (posthog-js : « client can always disable »).
  recordHeaders: false,
  recordBody: false,
  // Compressés, les snapshots arriveraient à `before_send` en chaîne gzip, et
  // leurs attributs partiraient sans être nettoyés.
  compress_events: false,
})

/** Les options de premier niveau de `posthog.init` qui portent le masquage. */
export const OPTIONS_MASQUAGE_POSTHOG = Object.freeze({
  // Autocapture : le texte et les attributs d'un élément cliqué (`data-text`
  // d'une liste, carte de contact entière) partiraient sans le masquage du
  // replay. Le replay montre de toute façon où la personne a cliqué.
  mask_all_text: true,
  mask_all_element_attributes: true,
  before_send: evenementSansDonnee,
})
